import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Select from "react-select";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Style.css";
import { TailSpin } from "react-loader-spinner";
import Add_transaction from "../../Components/Admin_components/add_transactions.js";
import Delete_transaction from "../../Components/Admin_components/delete_transaction.js";
import "../../Api/apiUrls";
import { getAdminTransaction, transactionExcel } from "../../Api/apiUrls";
import ViewTransactionPassScheduleBooked from "./viewTransactionPassScheduleBooked.js";
import ViewTransactionSplittedGiftcards from "./viewTransactionSplittedGiftcards.js";

function Transaction() {
  // Modal states
  const [modalAdd_transactionIsOpen, setAdd_transactionModalIsOpen] = useState(false);
  const [modalDelete_transactionIsOpen, setDelete_transactionIsOpen] = useState(false);
  const [modalUpdate_packagesIsOpen, setUpdate_packagesModalIsOpen] = useState(false);
  const [modalSplittesGiftcardPopupIsOpen, setSplittesGiftcardPopupIsOpen] = useState(false);
  
  // Selected items for modals
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [splittedGiftCards, setSplittedGiftCards] = useState(null);
  
  // Filter and search states
  const [selectedStatus, setSelectedStatus] = useState({ value: "All", label: "All" });
  const [selectedType, setSelectedType] = useState({ value: "All", label: "All" });
  const [selectedTransaction, setSelectedTransaction] = useState({ value: "All", label: "All" });
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  
  // Data states
  const [transactions, setTransactions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const observer = useRef();
  const searchInputRef = useRef();

  // Filter options
  const status = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const type = [
    { value: "All", label: "All" },
    { value: "Workshop", label: "Workshop" },
    { value: "Venue", label: "Venue" },
    { value: "Pass", label: "Pass" },
    { value: "Gift Card", label: "Gift Card" },
  ];

  const transaction = [
    { value: "All", label: "All" },
    { value: "Online", label: "Online" },
    { value: "GiftCard", label: "GiftCard" },
    { value: "Cash", label: "Cash" },
  ];

  // Modal handlers
  const openAdd_transaction = () => {
    setAdd_transactionModalIsOpen(true);
  };

  const closeAdd_transaction = () => {
    setAdd_transactionModalIsOpen(false);
  };

  const openDelete_transaction = (userId) => {
    setSelectedUserId(userId);
    setDelete_transactionIsOpen(true);
  };

  const closeDelete_transaction = () => {
    setDelete_transactionIsOpen(false);
  };

  const closeUpdate_packages = () => {
    setUpdate_packagesModalIsOpen(false);
  };

  const closeSplittesGiftcardPopupIsOpen = () => {
    setSplittesGiftcardPopupIsOpen(false);
  };

  const handleEyeClick = (id) => {
    setUpdate_packagesModalIsOpen(true);
    setSelectedPackageId(id);
  };

  const handleSplittedGiftcardsEyeClick = (gift_cards) => {
    setSplittesGiftcardPopupIsOpen(true);
    setSplittedGiftCards(gift_cards);
  };

  // Filter handlers
  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setPage(1);
    setTransactions([]);
  };

  const handleTypeChange = (selectedOption) => {
    setSelectedType(selectedOption);
    setPage(1);
    setTransactions([]);
  };

  const handleTransactionChange = (selectedOption) => {
    setSelectedTransaction(selectedOption);
    setPage(1);
    setTransactions([]);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchQuery(searchQuery);
    setPage(1);
    setTransactions([]);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setAppliedSearchQuery("");
    setPage(1);
    setTransactions([]);
  };

  // Build API query parameters
  const buildQueryParams = useCallback((pageNum) => {
    const params = new URLSearchParams();
    params.append('page', pageNum);
    params.append('page_size', 50);
    
    if (appliedSearchQuery) params.append('search', appliedSearchQuery);
    if (selectedStatus.value !== 'All') params.append('status', selectedStatus.value);
    if (selectedType.value !== 'All') params.append('type', selectedType.value);
    if (selectedTransaction.value !== 'All') params.append('mode', selectedTransaction.value);
    
    return params.toString();
  }, [appliedSearchQuery, selectedStatus, selectedType, selectedTransaction]);

  // Fetch data with pagination
  const fetchTransactions = useCallback(async (pageNum = 1) => {
    if (pageNum > pagination.total_pages && pagination.total_pages > 0) {
      setHasMore(false);
      return;
    }
    
    setIsFetching(true);
    try {
      const queryParams = buildQueryParams(pageNum);
      const response = await fetch(`${getAdminTransaction}?${queryParams}`);
      if (!response.ok) throw new Error("Network response was not ok");
      
      const data = await response.json();
      const newTransactions = data.data.Transaction || [];
      
      if (pageNum === 1) {
        setTransactions(newTransactions);
        setPagination(data.data.pagination || { current_page: 1, total_pages: 1 });
        setTotalItems(data.data.pagination?.total_items || 0);
      } else {
        setTransactions(prev => [...prev, ...newTransactions]);
      }
      
      setHasMore(pageNum < data.data.pagination?.total_pages);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [buildQueryParams, pagination.total_pages]);

  useEffect(() => {
    fetchTransactions(page);
  }, [page, fetchTransactions]);

  // Infinite scroll observer
  const lastTransactionElementRef = useCallback(node => {
    if (isFetching || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isFetching, hasMore]);

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "--";
    const createdAtDate = new Date(createdAt);
    if (isNaN(createdAtDate)) return "--";
    
    const datePart = createdAtDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    
    const timePart = createdAtDate.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    
    return `${datePart} - ${timePart}`;
  };

  const handleDownloadClick = () => {
    window.open(transactionExcel, "_blank");
  };

  useEffect(() => {
    const hasReloaded = localStorage.getItem('hasReloaded');
    if (!hasReloaded) {
      localStorage.setItem('hasReloaded', 'true');
      window.location.reload();
    }
  }, []);

  return (
    <div>
      <Header></Header>
      {modalUpdate_packagesIsOpen && (
        <ViewTransactionPassScheduleBooked
          id={selectedPackageId}
          onClose={closeUpdate_packages}
        />
      )}
      {modalSplittesGiftcardPopupIsOpen && (
        <ViewTransactionSplittedGiftcards
          gift_cards={splittedGiftCards}
          onClose={closeSplittesGiftcardPopupIsOpen}
        />
      )}
      {modalAdd_transactionIsOpen && (
        <Add_transaction onClose={closeAdd_transaction} />
      )}
      {modalDelete_transactionIsOpen && (
        <Delete_transaction
          userId={selectedUserId}
          onClose={closeDelete_transaction}
        />
      )}

      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Transaction</h1>
        </div>
        <section className="section">
          <div className="card p-2">
            <div className="parent col-md-12 row mt-2">
              <div className="row">
                <div className="child col-md-2" style={{ padding: "5px" }}>
                  <div className="input-group">
                    <input
                      type="text"
                      ref={searchInputRef}
                      style={{
                        borderRadius: "5px",
                        height: "37px",
                        padding: "10px",
                        border: "1px solid lightgrey ",
                        width: "100%",
                      }}
                      onChange={handleSearchChange}
                      onKeyPress={handleKeyPress}
                      value={searchQuery}
                      placeholder="Search..."
                    />
                    {searchQuery && (
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={clearSearch}
                        style={{ marginLeft: '5px' }}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                    <button 
                      className="btn btn-primary" 
                      type="button"
                      onClick={handleSearchSubmit}
                      style={{ marginLeft: '5px' }}
                    >
                      Search
                    </button>
                  </div>
                </div>
                <div className="col-md-2" style={{ padding: "5px" }}>
                  <Select
                    options={type}
                    placeholder="Select Type"
                    value={selectedType}
                    onChange={handleTypeChange}
                  />
                </div>
                <div className="col-md-2" style={{ padding: "5px" }}>
                  <Select
                    options={status}
                    placeholder="Select Status"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                  />
                </div>
                <div className="col-md-2" style={{ padding: "5px" }}>
                  <Select
                    options={transaction}
                    placeholder="Payment Mode"
                    value={selectedTransaction}
                    onChange={handleTransactionChange}
                  />
                </div>
                <div
                  className="col-md-2"
                  style={{ cursor: "pointer", padding: '5px', display: 'flex', alignItems: 'center' }}
                  onClick={handleDownloadClick}
                >
                  Download
                  <i
                    style={{ fontSize: "20px", marginLeft: "10px" }}
                    className="bi bi-download"
                  ></i>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {isLoading ? (
                <div style={{ marginTop: "50px", marginLeft: "50%" }}>
                  <TailSpin color="black" width={50} />
                </div>
              ) : transactions.length > 0 ? (
                <table className="table table-striped">
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <tr>
                      <th scope="col">Sr.No</th>
                      <th scope="col">Name of Listing</th>
                      <th scope="col">Type</th>
                      <th scope="col">Slot</th>
                      <th scope="col">Seats</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Customer Details</th>
                      <th scope="col">Transaction ID</th>
                      <th scope="col">Gift Card Code</th>
                      <th scope="col">Status of Transaction</th>
                      <th scope="col">Payment Mode</th>
                      <th scope="col">Created On</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody id="tableBody">
                    {transactions.map((transaction, index) => (
                      <tr 
                        key={`${transaction.id}-${index}`}
                        ref={index === transactions.length - 1 ? lastTransactionElementRef : null}
                      >
                        <th scope="row">{(pagination.current_page - 1) * 50 + index + 1}</th>
                        <td>
                          {transaction.title || "--"}
                          <br />
                          {transaction.order_id || "--"}
                        </td>
                        <td>
                          {transaction.type || "--"}
                        </td>
                        <td>
                          {transaction?.slot || "--"}
                          <br />
                          {transaction?.type === "Venue"
                            ? formatCreatedAt(transaction?.bookingSlot?.startDate)
                            : formatCreatedAt(transaction?.workshopSlot?.date)}
                        </td>
                        <td>
                          {transaction.seats || "--"}
                        </td>
                        <td>
                          {transaction.amount || "--"}
                        </td>
                        <td>
                          {transaction.user ? (
                            <>
                              {transaction.user.name || "--"}
                              <br />
                              {transaction.user.mobileNumber || "--"}
                              <br />
                              {transaction.user.email || "--"}
                            </>
                          ) : transaction.GiftCard ? (
                            <>
                              <b>Not User</b>
                              <br />
                              {transaction.GiftCard.name || "--"}
                              <br />
                              {transaction.GiftCard.mobileNumber || "--"}
                              <br />
                              {transaction.GiftCard.email || "--"}
                            </>
                          ) : (
                            "Not User"
                          )}
                        </td>
                        <td>{transaction.payment_id || "--"}</td>
                        <td style={{ textAlign: "center" }}>
                          {transaction?.gift_cards?.length > 0 ? (
                            <a onClick={() => handleSplittedGiftcardsEyeClick(transaction.gift_cards)}>
                              <i className="bi bi-eye-fill"></i>
                            </a>
                          ) : transaction?.mode === "GiftCard" ? (
                            transaction?.GiftCard?.card_code ? (
                              transaction.GiftCard.card_code
                            ) : (
                              "--"
                            )
                          ) : (
                            "--"
                          )}
                        </td>
                        <td>
                          {transaction.transaction_status || "--"}
                        </td>
                        <td>
                          {transaction.mode || "--"}
                        </td>
                        <td>
                          {formatCreatedAt(transaction.created_at)}
                        </td>
                        <td style={{textAlign:'center'}}>
                          <a onClick={() => handleEyeClick(transaction.id)}>
                            <i className="bi bi-eye-fill"></i>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <h3 style={{ textAlign: "center" }}> No data available </h3>
              )}

              {isFetching && (
                <div className="text-center py-3">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              )}
            </div>

            <div className="p-3 bg-dark text-white rounded-bottom">
              Showing {transactions.length} of {totalItems} total transactions
              {hasMore && " - scroll to load more"}
            </div>
          </div>
        </section>
      </main>
      <Footer></Footer>
    </div>
  );
}

export default Transaction;